import type {
  SearchCriteriaFilterSchema,
  SearchCriteriaRangeFilterSchema,
  SearchCriteriaTerm,
  SearchSearchCriteriaSchema,
} from '@iconik-io/iconik-api/src/services/search';
import {
  type CanonicalFilterMapping,
  EXCLUDE_ALL_OF,
  EXCLUDE_ANY_OF,
  type FilterValue,
  type FilterValueOperator,
  type FilterValues,
  INCLUDE_ALL_OF,
  INCLUDE_ANY_OF,
  type MultiSelectFilterGroup,
  type MultiSelectFilterModifier,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';
import { isMultiSelectFilterValue } from '@iconik-io/shared-lib/src/utils/search/guard_multi_select_filter';

import {
  DEFAULT_CANONICAL_MAPPINGS,
  DEFAULT_MULTI_SELECT_FIELD_NAMES,
} from '../../constants/search_mappings';
import { flattenSearchCriteriaFilters } from './flatten_search_criteria_filters';

// Criteria read back from the API type `operator` as a plain string. Anything
// other than a negation behaves as a conjunction downstream.
const toFilterValueOperator = (operator: string): FilterValueOperator =>
  operator === 'NOT' ? 'NOT' : 'AND';

const toModifier = (
  operator: FilterValueOperator,
  isSingleValue: boolean,
): MultiSelectFilterModifier => {
  if (operator === 'NOT') {
    return isSingleValue ? EXCLUDE_ALL_OF : EXCLUDE_ANY_OF;
  }
  return isSingleValue ? INCLUDE_ALL_OF : INCLUDE_ANY_OF;
};

const processTermValues = (
  termName: string,
  termValues: string | string[] | SearchCriteriaRangeFilterSchema,
  operator: FilterValueOperator,
  isSingleValue: boolean,
  mapping?: CanonicalFilterMapping,
  isMultiSelect?: boolean,
): FilterValue => {
  const isMapped = mapping && mapping.nestedKeys.has(termName);

  if (!isMapped && !isMultiSelect) {
    return termValues;
  }

  // A range bound carries no discrete items, so it can never form a group.
  if (typeof termValues !== 'string' && !Array.isArray(termValues)) {
    return termValues;
  }

  const items = Array.isArray(termValues) ? termValues : [termValues];
  const group: MultiSelectFilterGroup = {
    modifier: toModifier(operator, isSingleValue),
    items,
  };

  if (isMapped && termName !== mapping.canonicalKey) {
    group.itemTermNames = Object.fromEntries(items.map((id) => [id, termName]));
  }

  return [group];
};

const appendTermsToFilterValues = (
  filterValues: FilterValues,
  terms: SearchCriteriaTerm[] | undefined,
  operator: FilterValueOperator,
  mappings: CanonicalFilterMapping[],
  multiSelectFieldNames: ReadonlySet<string>,
) => {
  if (!terms || terms.length === 0) {
    return filterValues;
  }

  const result: FilterValues = { ...filterValues };
  const lastTermNameForKey = new Map<string, string>();

  for (const term of terms) {
    // A criteria document read back from the API leaves absent range bounds as
    // null; filter values only ever carry them as absent.
    const range: SearchCriteriaRangeFilterSchema | undefined = term.range && {
      max: term.range.max ?? undefined,
      min: term.range.min ?? undefined,
      timezone: term.range.timezone ?? undefined,
    };
    const value = term.value ?? term.value_in ?? range ?? [];

    // Find if this specific term belongs to any of our nested mappings
    const activeMapping = mappings.find((mapping) =>
      mapping.nestedKeys.has(term.name),
    );

    const resultKey = activeMapping ? activeMapping.canonicalKey : term.name;

    const processedValues = processTermValues(
      term.name,
      value,
      operator,
      term.value !== undefined,
      activeMapping,
      multiSelectFieldNames.has(resultKey),
    );

    const existing = result[resultKey];

    if (
      isMultiSelectFilterValue(existing) &&
      isMultiSelectFilterValue(processedValues)
    ) {
      // Consolidate single-item groups into the last group when the modifier
      // matches. For mapped fields (e.g. storage), different nested term
      // names (files.storage_id, proxies.storage_id) all map to the same
      // canonical key and should consolidate. For non-mapped fields we only
      // consolidate consecutive terms with the same term.name.
      const incomingGroup = processedValues[0];
      const lastGroup = existing.at(-1);
      if (
        processedValues.length === 1 &&
        lastGroup &&
        lastGroup.modifier === incomingGroup.modifier &&
        (activeMapping || lastTermNameForKey.get(resultKey) === term.name)
      ) {
        lastGroup.items = [...lastGroup.items, ...incomingGroup.items];
        if (incomingGroup.itemTermNames) {
          lastGroup.itemTermNames = {
            ...lastGroup.itemTermNames,
            ...incomingGroup.itemTermNames,
          };
        }
      } else {
        result[resultKey] = [...existing, ...processedValues];
      }
    } else {
      result[resultKey] = processedValues;
    }

    lastTermNameForKey.set(resultKey, term.name);
  }

  return result;
};

/**
 * Harvests flat and nested mapped items into a unified UI configuration.
 */
function harvestMappedTerms(
  terms: SearchCriteriaTerm[],
  mapping: CanonicalFilterMapping,
) {
  const items: string[] = [];
  const itemTermNames: Record<string, string> = {};
  const additionalTermNames = new Set<string>();

  for (const term of terms) {
    if (term.name === mapping.canonicalKey) {
      additionalTermNames.add(term.name);
      continue;
    }

    const termItems = term.value_in ?? (term.value ? [term.value] : []);
    for (const item of termItems) {
      if (!itemTermNames[item]) {
        items.push(item);
        itemTermNames[item] = term.name;
      }
    }
  }

  return {
    items,
    itemTermNames,
    additionalTermNames: Array.from(additionalTermNames),
  };
}

const extractMappedExcludeGroups = (
  filters: SearchCriteriaFilterSchema[],
  mapping: CanonicalFilterMapping,
): {
  remainingFilters: SearchCriteriaFilterSchema[];
  mappedGroups: MultiSelectFilterGroup[];
} => {
  const remainingFilters: SearchCriteriaFilterSchema[] = [];
  const mappedGroups: MultiSelectFilterGroup[] = [];

  for (const filter of filters) {
    if (
      filter.operator !== 'NOT' ||
      filter.terms?.length ||
      filter.filters?.length !== 1
    ) {
      remainingFilters.push(filter);
      continue;
    }

    const innerFilter = filter.filters[0];
    const allTerms = [
      ...(innerFilter.terms ?? []),
      ...(innerFilter.filters?.flatMap((f) => f.terms ?? []) ?? []),
    ];

    if (allTerms.length === 0) {
      remainingFilters.push(filter);
      continue;
    }

    const isExclusivelyMapped = allTerms.every(
      (t) =>
        (mapping.nestedKeys.has(t.name) || t.name === mapping.canonicalKey) &&
        (t.value !== undefined || t.value_in !== undefined),
    );
    const hasNestedTerm = allTerms.some((t) => mapping.nestedKeys.has(t.name));

    if (isExclusivelyMapped && hasNestedTerm) {
      const harvested = harvestMappedTerms(allTerms, mapping);

      const nestedTerms = allTerms.filter((t) =>
        mapping.nestedKeys.has(t.name),
      );
      const isSingleValue =
        nestedTerms.length > 0 &&
        nestedTerms.every((t) => t.value !== undefined);
      const modifier: MultiSelectFilterGroup['modifier'] = isSingleValue
        ? 'exclude_all_of'
        : 'exclude_any_of';

      const group: MultiSelectFilterGroup = {
        modifier,
        items: harvested.items,
        itemTermNames: harvested.itemTermNames,
      };

      if (harvested.additionalTermNames.length > 0) {
        group.additionalTermNames = harvested.additionalTermNames;
      }

      mappedGroups.push(group);
    } else {
      remainingFilters.push(filter);
    }
  }

  return { remainingFilters, mappedGroups };
};

const extractMappedIncludeGroups = (
  filters: SearchCriteriaFilterSchema[],
  mapping: CanonicalFilterMapping,
): {
  remainingFilters: SearchCriteriaFilterSchema[];
  mappedGroups: MultiSelectFilterGroup[];
} => {
  const remainingFilters: SearchCriteriaFilterSchema[] = [];
  const mappedGroups: MultiSelectFilterGroup[] = [];

  for (const filter of filters) {
    const hasDeepNesting = filter.filters?.some(
      (item) => (item.filters?.length ?? 0) > 0,
    );
    if (filter.operator !== 'OR' || hasDeepNesting) {
      remainingFilters.push(filter);
      continue;
    }

    const allTerms = [
      ...(filter.terms ?? []),
      ...(filter.filters?.flatMap((f) => f.terms ?? []) ?? []),
    ];

    const isExclusivelyMapped =
      allTerms.length > 0 &&
      allTerms.every(
        (t) =>
          (mapping.nestedKeys.has(t.name) || t.name === mapping.canonicalKey) &&
          (t.value !== undefined || t.value_in !== undefined),
      );
    const hasNestedTerm = allTerms.some((t) => mapping.nestedKeys.has(t.name));

    if (isExclusivelyMapped && hasNestedTerm) {
      const harvested = harvestMappedTerms(allTerms, mapping);

      const nestedTerms = allTerms.filter((t) =>
        mapping.nestedKeys.has(t.name),
      );
      const isSingleValue =
        nestedTerms.length > 0 &&
        nestedTerms.every((t) => t.value !== undefined);
      const modifier: MultiSelectFilterGroup['modifier'] = isSingleValue
        ? 'include_all_of'
        : 'include_any_of';

      const group: MultiSelectFilterGroup = {
        modifier,
        items: harvested.items,
        itemTermNames: harvested.itemTermNames,
      };

      if (harvested.additionalTermNames.length > 0) {
        group.additionalTermNames = harvested.additionalTermNames;
      }

      mappedGroups.push(group);
    } else {
      remainingFilters.push(filter);
    }
  }

  return { remainingFilters, mappedGroups };
};

const isFilterSupported = (filter: SearchCriteriaFilterSchema) => {
  if (
    filter.terms?.some((t) => t.missing !== undefined || t.exists !== undefined)
  ) {
    return false;
  }

  if (filter.operator === 'NOT') {
    const hasTerms = (filter.terms?.length ?? 0) > 0;
    const allSimple = filter.terms?.every(
      (t) => t.value !== undefined || (t.value_in && t.value_in.length > 0),
    );
    return hasTerms && allSimple;
  }

  return true;
};

export const criteriaToFilterValues = (
  criteria: SearchSearchCriteriaSchema | undefined,
  canonicalFilterMappings: CanonicalFilterMapping[] = DEFAULT_CANONICAL_MAPPINGS,
  multiSelectFieldNames: ReadonlySet<string> = DEFAULT_MULTI_SELECT_FIELD_NAMES,
): {
  filterValues: FilterValues;
  unsupportedFilters: SearchCriteriaFilterSchema[];
} => {
  let filterValues: FilterValues = {};
  const unsupportedFilters: SearchCriteriaFilterSchema[] = [];

  if (!criteria) {
    return { filterValues, unsupportedFilters };
  }

  const flattenedCriteria = flattenSearchCriteriaFilters(criteria);
  const { filter: baseFilter } = flattenedCriteria;

  if (!baseFilter) {
    return { filterValues, unsupportedFilters };
  }

  // Process base level terms against ALL mappings
  filterValues = appendTermsToFilterValues(
    filterValues,
    baseFilter.terms,
    toFilterValueOperator(baseFilter.operator),
    canonicalFilterMappings,
    multiSelectFieldNames,
  );

  let currentFilters = baseFilter.filters ?? [];

  // Extract nested/grouped filters sequentially for EACH mapping
  for (const mapping of canonicalFilterMappings) {
    const { remainingFilters: afterInclude, mappedGroups: includeGroups } =
      extractMappedIncludeGroups(currentFilters, mapping);

    const { remainingFilters: afterExclude, mappedGroups: excludeGroups } =
      extractMappedExcludeGroups(afterInclude, mapping);

    // Shrink the filter pool so the next mapping doesn't double-process them
    currentFilters = afterExclude;

    const allMappedGroups = [...includeGroups, ...excludeGroups];

    if (allMappedGroups.length > 0) {
      const existing = filterValues[mapping.canonicalKey];
      filterValues[mapping.canonicalKey] = isMultiSelectFilterValue(existing)
        ? existing.concat(allMappedGroups)
        : allMappedGroups;
    }
  }

  // Process standard filters that are left after extracting all mapped groups
  for (const filter of currentFilters) {
    // Handle NOT > [single inner filter with terms] (e.g. exclude_all_of
    // serialized as NOT > AND > individual value terms)
    if (
      filter.operator === 'NOT' &&
      !filter.terms?.length &&
      filter.filters?.length === 1
    ) {
      const innerFilter = filter.filters[0];
      const innerTerms = innerFilter.terms ?? [];
      const allSimple =
        innerTerms.length > 0 &&
        innerTerms.every(
          (t) => t.value !== undefined || (t.value_in && t.value_in.length > 0),
        );
      if (allSimple) {
        filterValues = appendTermsToFilterValues(
          filterValues,
          innerTerms,
          'NOT',
          canonicalFilterMappings,
          multiSelectFieldNames,
        );
        continue;
      }
    }

    if (!isFilterSupported(filter)) {
      unsupportedFilters.push(filter);
      continue;
    }
    filterValues = appendTermsToFilterValues(
      filterValues,
      filter.terms,
      toFilterValueOperator(filter.operator),
      canonicalFilterMappings,
      multiSelectFieldNames,
    );
  }

  return { filterValues, unsupportedFilters };
};
